import { type ReactNode } from 'react';
import type { FlowNodeData } from './flow.types.js';
import './NodeInspector.css';

export interface NodeInspectorProps {
  node: FlowNodeData | null;
  /** Renderer keyed on `node.type`. Each renderer returns the editing form. */
  inspectors?: Record<string, (props: { node: FlowNodeData; onChange: (data: Record<string, unknown>) => void }) => ReactNode>;
  /** Called when the consumer's form mutates the node's `data`. */
  onChange?: (data: Record<string, unknown>) => void;
  /** Called when the consumer requests deletion. */
  onDelete?: (id: string) => void;
  /** Title above the form. */
  title?: ReactNode;
  /** Visible when no node is selected. */
  emptyState?: ReactNode;
  className?: string;
}

/**
 * Right-side inspector panel for the active node on `<FlowCanvas>`.
 * Consumers register a renderer per node type via `inspectors`: sdk-ui
 * doesn't ship per-domain forms (`AskQuestionNode`, `LlmNode`, etc.); the
 * **app** owns that schema.
 */
export function NodeInspector({ node, inspectors = {}, onChange, onDelete, title, emptyState, className }: NodeInspectorProps): ReactNode {
  return (
    <aside className={['rr-node-inspector', className].filter(Boolean).join(' ')} aria-label="Node properties">
      {title ? <div className="rr-node-inspector__title">{title}</div> : null}
      {!node ? (
        <div className="rr-node-inspector__empty">{emptyState ?? 'Select a node to edit.'}</div>
      ) : (
        <>
          <div className="rr-node-inspector__header">
            <span className="rr-node-inspector__type">{node.type}</span>
            <span className="rr-node-inspector__id">#{node.id}</span>
          </div>
          <div className="rr-node-inspector__body">
            {inspectors[node.type]
              ? inspectors[node.type]({ node, onChange: (data) => onChange?.(data) })
              : <div className="rr-node-inspector__hint">No inspector registered for `{node.type}`.</div>}
          </div>
          {onDelete ? (
            <button
              type="button"
              className="rr-node-inspector__delete"
              onClick={() => onDelete(node.id)}
            >
              Delete node
            </button>
          ) : null}
        </>
      )}
    </aside>
  );
}
